import React, { Fragment, useEffect } from 'react';
import {
  FormData,
  FormErrors,
  IResolvePlannedActivityDialog,
} from './interfaces';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../../../../components/Dialog';
import { Form } from '../../../../components/forms';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import useFormState from '../../../../hooks/useFormState';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import { DueDateTimeSection } from '../components/DueDateTimeSection';
import { DueDateTimeSectionFormData } from '../components/DueDateTimeSection/interfaces';
import { atLeast, isEmpty } from '../../../../helpers/formHelpers';
import { isTodayOrFuture, isValidDate } from '../../../../helpers/dateHelpers';
import { Dayjs } from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../../../routes/Routes';
import { useResolvePlannedActivityMutation } from '../../../../reactQuery/Activities/activities';

export const ResolvePlannedActivityDialog: React.FC<
  IResolvePlannedActivityDialog
> = ({ show, onClose, plannedActivityId, title }): JSX.Element => {
  const { t } = useTranslation();
  const formId = 'resolveActivityForm';
  const navigate = useNavigate();

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>({
    timeType: 'no-time',
    resolved: 'yes',
    includeInContactInterval: 'no',
  });
  const { formErrors, setFormErrors } = useTrackFormChanges<FormData>(formData);

  const {
    mutate: resolvePlannedActivity,
    isLoading: isResolvingPlannedActivity,
  } = useResolvePlannedActivityMutation();

  const onSubmit = () => {
    if (!Object.keys(formErrors).length) {
      const resolveData = {
        newTitle: formData.title ? formData.title : null,
        summary: formData.summary as string,
        performAt: formData.performAt as Dayjs,
        startTime: formData.timeType !== 'no-time' ? formData.startTime : null,
        endTime: formData.timeType === 'range' ? formData.endTime : null,
        resolved: formData.resolved === 'yes',
        excludeFromContactInterval: formData.includeInContactInterval === 'no',
        remainingWork: formData.remainingWork,
      };

      resolvePlannedActivity(
        {
          plannedActivityId: plannedActivityId,
          resolveData,
        },
        {
          onSuccess: (activityLogId) => {
            enqueueSnackbar(t('forms:resolvePlannedActivity.success'), {
              variant: 'success',
            });

            navigate(ROUTES.activityLog.details(activityLogId));
            onClose();
          },
          onError: () => {
            enqueueSnackbar(t('forms:resolvePlannedActivity.failed'), {
              variant: 'error',
            });
          },
        }
      );
    }
  };

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.summary || isEmpty(formData.summary)) {
      errors.summary = t('errors.required');
    } else if (!atLeast(formData.summary, 10)) {
      errors.summary = t('errors.atLeast', {
        field: t('entities:activityLog.summary').toLowerCase(),
        min: 10,
      });
    }

    if (formData.resolved === 'no') {
      if (!formData.title || isEmpty(formData.title)) {
        errors.title = t('errors.required');
      } else if (!atLeast(formData.title, 10)) {
        errors.title = t('errors.atLeast', {
          field: t('entities:plannedActivity.title').toLowerCase(),
          min: 10,
        });
      }

      if (formData.performAt === null || formData.performAt === undefined) {
        errors.performAt = t('errors.required');
      } else if (!isValidDate(formData.performAt)) {
        errors.performAt = t('errors.invalidDate');
      } else if (!isTodayOrFuture(formData.performAt, true)) {
        errors.performAt = t('errors.dateNeedsToBeTodayOrInTheFuture');
      }

      if (formData.timeType === 'exact' && !formData.startTime) {
        errors.startTime = t('errors.required');
      }

      if (formData.timeType === 'range' && !formData.startTime) {
        errors.startTime = t('errors.required');
      }

      if (formData.timeType === 'range' && !formData.endTime) {
        errors.endTime = t('errors.required');
      }

      if (!formData.remainingWork || isEmpty(formData.remainingWork)) {
        errors.remainingWork = t('errors.required');
      } else if (!atLeast(formData.remainingWork, 10)) {
        errors.remainingWork = t('errors.atLeast', {
          field: t(
            'forms:resolvePlannedActivity.fields.remainingWork.name'
          ).toLowerCase(),
          min: 10,
        });
      }
    }

    setFormErrors(errors);
  };

  const buttons = (
    <Fragment>
      <Button onClick={onClose} disabled={isResolvingPlannedActivity}>
        {t('buttons.cancel')}
      </Button>
      <Button
        form={formId}
        type="submit"
        variant="contained"
        onClick={validateForm}
        disabled={isResolvingPlannedActivity}
      >
        {formData.resolved === 'yes'
          ? t('forms:resolvePlannedActivity.buttons.resolve')
          : t('forms:resolvePlannedActivity.buttons.pending')}
      </Button>
    </Fragment>
  );

  useEffect(() => {
    setFormErrors({});

    setFormData({
      timeType: 'no-time',
      resolved: 'yes',
      includeInContactInterval: 'no',
      title,
    });
  }, [show]);

  return (
    <Dialog
      show={show}
      onClose={onClose}
      buttons={buttons}
      title={t('forms:resolvePlannedActivity.title')}
      disableClose={isResolvingPlannedActivity}
    >
      <Form id={formId} onSubmit={onSubmit}>
        <TextField
          label={t('forms:resolvePlannedActivity.fields.summary.label')}
          placeholder={t(
            'forms:resolvePlannedActivity.fields.summary.placeholder'
          )}
          variant="outlined"
          size="small"
          value={formData.summary}
          onChange={(e) => setFormFieldData('summary', e.target.value)}
          error={!!formErrors.summary}
          helperText={formErrors.summary}
          multiline
          minRows={4}
          maxRows={10}
          required
        />

        <FormControl>
          <FormLabel id="radio-group-exclude-from-contact-interval">
            {t(
              'forms:resolvePlannedActivity.fields.exclude-from-contact-interval.label'
            )}
          </FormLabel>
          <RadioGroup
            aria-labelledby="radio-group-exclude-from-contact-interval"
            name="exclude-from-contact-interval"
            value={formData.includeInContactInterval}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFormFieldData(
                'includeInContactInterval',
                (event.target as HTMLInputElement)
                  .value as FormData['includeInContactInterval']
              );
            }}
          >
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label={t(
                'forms:resolvePlannedActivity.fields.exclude-from-contact-interval.options.yes'
              )}
            />
            <FormControlLabel
              value="no"
              control={<Radio />}
              label={t(
                'forms:resolvePlannedActivity.fields.exclude-from-contact-interval.options.no'
              )}
            />
          </RadioGroup>
        </FormControl>

        <FormControl>
          <FormLabel id="radio-group-resolved">
            {t('forms:resolvePlannedActivity.fields.resolved.label')}
          </FormLabel>
          <RadioGroup
            aria-labelledby="radio-group-resolved"
            name="resolved"
            value={formData.resolved}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFormFieldData(
                'resolved',
                (event.target as HTMLInputElement).value as FormData['resolved']
              )
            }
          >
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label={t(
                'forms:resolvePlannedActivity.fields.resolved.options.yes'
              )}
            />
            <FormControlLabel
              value="no"
              control={<Radio />}
              label={t(
                'forms:resolvePlannedActivity.fields.resolved.options.no'
              )}
            />
          </RadioGroup>
        </FormControl>

        {formData.resolved === 'no' && (
          <Fragment>
            <TextField
              label={t('entities:plannedActivity.title')}
              variant="outlined"
              size="small"
              value={formData.title}
              placeholder={title}
              onChange={(e) => setFormFieldData('title', e.target.value)}
              error={!!formErrors.title}
              helperText={formErrors.title}
              required
            />

            <TextField
              label={t(
                'forms:resolvePlannedActivity.fields.remainingWork.label'
              )}
              placeholder={t(
                'forms:resolvePlannedActivity.fields.remainingWork.placeholder'
              )}
              variant="outlined"
              size="small"
              value={formData.remainingWork}
              onChange={(e) =>
                setFormFieldData('remainingWork', e.target.value)
              }
              error={!!formErrors.remainingWork}
              helperText={formErrors.remainingWork}
              multiline
              minRows={4}
              maxRows={10}
              required
            />

            <DueDateTimeSection
              formData={formData}
              formErrors={formErrors}
              disablePast
              setFormFieldData={
                setFormFieldData as <
                  K extends keyof DueDateTimeSectionFormData
                >(
                  field: K,
                  value: DueDateTimeSectionFormData[K]
                ) => void
              }
            />
          </Fragment>
        )}
      </Form>
    </Dialog>
  );
};
